import type { ElementRef } from '@angular/core';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { Duration } from 'luxon';
import { EMPTY, interval, skip, switchMap, take } from 'rxjs';
import { OnyxTheme } from '../../../../enums';
import { OnyxThemeService } from '../../../../services/onyx-theme.service';
import { OnyxButtonComponent } from '../../../buttons/onyx-button/onyx-button.component';
import { OnyxCloseButtonComponent } from '../../../buttons/onyx-close-button/onyx-close-button.component';
import type { OnyxIconName } from '../../../icons/interfaces';
import { OnyxIconComponent } from '../../../icons/onyx-icon/onyx-icon.component';
import { OnyxToastIntent } from '../../enums';
import type { OnyxToastButton, OnyxToastLayout } from '../../interfaces';

@Component({
  selector: 'onyx-toast',
  imports: [
    OnyxIconComponent,
    OnyxButtonComponent,
    OnyxCloseButtonComponent,
    TranslatePipe,
  ],
  templateUrl: './onyx-toast.component.html',
  styleUrl: './onyx-toast.component.scss',
  host: {
    '[class.front]': `layout().index === 0`,
    '[class.expanded]': `layout().expanded`,
    '[style.--index]': `layout().index`,
    '[style.--offset.px]': `layout().offset`,
    '[style.--front-height.px]': `layout().frontHeight`,
    '[style.--height.px]': `height()`,
  },
})
export class OnyxToastComponent {
  private readonly themeService = inject(OnyxThemeService);

  public readonly intent = input.required<OnyxToastIntent>();
  public readonly icon = input<OnyxIconName>();
  public readonly message = input.required<string>();
  public readonly description = input<string>();
  public readonly buttons = input<OnyxToastButton[]>();
  public readonly layout = input.required<OnyxToastLayout>();
  public readonly paused = input.required<boolean>();

  public readonly heightChange = output<number>();
  public readonly closeToast = output<void>();

  private readonly bodyRef =
    viewChild.required<ElementRef<HTMLElement>>('body');

  private readonly TOAST_DURATION = Duration.fromObject({
    seconds: 10,
  }).toMillis();
  private readonly TIMER_TICK = 100;

  protected height = signal<number | undefined>(undefined);

  protected isInverse = computed(
    () => this.themeService.theme() === OnyxTheme.LIGHT,
  );
  protected toastIcon = computed(() => {
    const iconMap = {
      [OnyxToastIntent.SUCCESS]: 'check-circle-filled',
      [OnyxToastIntent.ERROR]: 'error-circle-filled',
      [OnyxToastIntent.WARNING]: 'alert-circle-filled',
      [OnyxToastIntent.INFORMATION]: 'tooltip-filled',
    } as const;

    return this.icon() ?? iconMap[this.intent()];
  });

  constructor() {
    const destroyRef = inject(DestroyRef);

    toObservable(this.paused)
      .pipe(
        switchMap((paused) => (paused ? EMPTY : interval(this.TIMER_TICK))),
        skip(this.TOAST_DURATION / this.TIMER_TICK - 1),
        take(1),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.closeToast.emit());

    afterNextRender(() => {
      const observer = new ResizeObserver(([entry]) => {
        const height = entry.borderBoxSize[0].blockSize;
        this.height.set(height);
        this.heightChange.emit(height);
      });
      observer.observe(this.bodyRef().nativeElement);
      destroyRef.onDestroy(() => observer.disconnect());
    });
  }
}
